/* The only place this app talks to the service. Two shapes, because the service has two: GET
 * routes answer JSON, and every write is a form post answering `{ok: true}` or a 4xx with a
 * sentence in `detail`.
 */

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Called when the service says the session is gone. Registered by the app root, which swaps
 *  the dashboard for the sign-in page rather than issuing a redirect fetch() cannot follow. */
let onUnauthenticated: () => void = () => {};

export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

export type Params = Record<string, string | number | boolean | null | undefined>;

function url(path: string, params: Params = {}): string {
  const u = new URL(path, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null) u.searchParams.set(k, String(v));
  });
  return u.toString();
}

/** Requests in flight, keyed by URL. A coalescer, not a cache: entries are removed as soon
 *  as the request settles, so two readers of one endpoint cost one query and nothing here
 *  goes stale. */
const inflight = new Map<string, Promise<unknown>>();

export function api<T>(path: string, params: Params = {}): Promise<T> {
  const key = url(path, params);
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;

  const request = (async () => {
    const res = await fetch(key, { credentials: 'same-origin' });
    if (res.status === 401) {
      onUnauthenticated();
      throw new ApiError(401, 'Not signed in.');
    }
    if (!res.ok) throw new ApiError(res.status, `${path} → ${res.status}`);
    return res.json();
  })();

  inflight.set(key, request);
  request.finally(() => inflight.delete(key)).catch(() => {});
  return request as Promise<T>;
}

export interface WriteResult<T = Record<string, unknown>> {
  ok: boolean;
  data: T;
}

export type Fields = Record<string, string | Blob | null | undefined>;

/** POST (or DELETE) a form-encoded body and hand back `{ok, data}`. The write routes all
 *  answer the same shape, so one helper serves all of them. */
export async function write<T = Record<string, unknown>>(
  path: string,
  fields: Fields = {},
  method: 'POST' | 'DELETE' = 'POST',
): Promise<WriteResult<T>> {
  const body = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v != null) body.set(k, v);
  });

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      // DELETE carries no fields, and a multipart body on it is the sort of thing a proxy
      // between here and the server is entitled to drop.
      body: method === 'DELETE' ? undefined : body,
      credentials: 'same-origin',
    });
  } catch {
    return { ok: false, data: { detail: 'Could not reach the server.' } as T };
  }

  const data = (await res.json().catch(() => ({}))) as T;
  if (res.status === 401) onUnauthenticated();
  return { ok: res.ok, data };
}

/** The sentence a refused write came back with, or a fallback. */
export const detailOf = (data: unknown, fallback: string): string => {
  const detail = (data as { detail?: unknown } | null)?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};
